/**
 * SourcePane — right-side pane that hosts the source/action viewer.
 *
 * Layout shape:
 *   ┌──────────────────────────────────────┐
 *   │  Tab Bar  (clickable, most-recent first, X to close)
 *   ├──────────────────────────────────────┤
 *   │                                      │
 *   │  <SourceRenderer> for active tab     │
 *   │  or                                  │
 *   │  <ActionCard> when pendingAction set │
 *   │                                      │
 *   └──────────────────────────────────────┘
 *   ⬄ draggable divider on the left edge persists width
 *
 * Collapses to a thin rail on the left edge with a "Sources" affordance.
 * Mobile (< md): becomes a bottom sheet covering 80vh.
 *
 * Keyboard: Esc collapses, [ and ] cycle tabs — wired in SourcePaneContext.
 */

import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, Files, PanelRightClose, ShieldAlert, X } from 'lucide-react'
import { useSourcePane } from '../../contexts/SourcePaneContext'
import { SOURCE_KIND_META, type Citation } from '../../types/cards'
import SourceRenderer from './SourceRenderer'
import ActionCardView from './ActionCard'
import ApprovalCountdown from './ApprovalCountdown'

const MIN_WIDTH = 320

export default function SourcePane() {
  const {
    openSources,
    activeSourceId,
    activeSource,
    isCollapsed,
    width,
    pendingAction,
    pendingActionExpiresAt,
    mode,
    activateSource,
    closeSource,
    setCollapsed,
    setWidth,
  } = useSourcePane()

  const hasContent = openSources.length > 0 || !!pendingAction

  // When there's nothing to show, render nothing at all (no rail, no flicker).
  // The chat reclaims the full width.
  if (!hasContent) return null

  // Collapsed → render a thin rail with a single button to re-expand.
  //
  // A pending approval gets its OWN indicator rather than sharing the sources
  // icon. Parking a review is the point of collapsing, but until this existed
  // the collapsed rail looked identical whether an approval was waiting or not
  // — so a parked approval was invisible and expired without warning. The
  // remaining time is on the rail for the same reason.
  if (isCollapsed) {
    const awaitingApproval = !!pendingAction
    return (
      <aside
        role="complementary"
        aria-label={
          awaitingApproval
            ? 'Sources panel (collapsed, approval waiting)'
            : 'Sources panel (collapsed)'
        }
        className="hidden md:flex border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark w-10 flex-col items-center py-3 gap-2"
      >
        {awaitingApproval && (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Review the action waiting for your approval"
            title={`Waiting for your approval: ${pendingAction?.title ?? 'action'}`}
            className="p-1.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60 relative"
          >
            <ShieldAlert size={18} />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500" />
          </button>
        )}
        {awaitingApproval && pendingActionExpiresAt && (
          <span
            className="text-[9px] leading-tight text-center px-0.5 [writing-mode:vertical-rl] rotate-180"
            aria-hidden="true"
          >
            <ApprovalCountdown expiresAt={pendingActionExpiresAt} />
          </span>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Expand sources panel"
          title={`Sources (${openSources.length})`}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-surface-dark-overlay text-gray-500 dark:text-gray-400 relative"
        >
          <Files size={18} />
          {openSources.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-amtrend-blue text-white text-[10px] font-medium flex items-center justify-center">
              {openSources.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="Open most recent source"
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-surface-dark-overlay text-gray-500 dark:text-gray-400"
        >
          <ChevronLeft size={18} />
        </button>
      </aside>
    )
  }

  return (
    <>
      {/* Mobile bottom sheet */}
      <MobileSheet />
      {/* Desktop pane */}
      <DesktopPane
        width={width}
        setWidth={setWidth}
        openSources={openSources}
        activeSourceId={activeSourceId}
        activeSource={activeSource}
        activateSource={activateSource}
        closeSource={closeSource}
        onCollapse={() => setCollapsed(true)}
        mode={mode}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Desktop variant — visible at md+
// ---------------------------------------------------------------------------

interface DesktopProps {
  width: number
  setWidth: (w: number) => void
  openSources: Citation[]
  activeSourceId: string | null
  activeSource: Citation | null
  activateSource: (id: string) => void
  closeSource: (id: string) => void
  onCollapse: () => void
  mode: 'source' | 'action'
}

function DesktopPane({
  width,
  setWidth,
  openSources,
  activeSourceId,
  activeSource,
  activateSource,
  closeSource,
  onCollapse,
  mode,
}: DesktopProps) {
  const [isResizing, setIsResizing] = useState(false)

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    const startX = e.clientX
    const startWidth = width

    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX // dragging left → wider
      const next = Math.max(MIN_WIDTH, startWidth + delta)
      setWidth(next)
    }
    const onUp = () => {
      setIsResizing(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [width, setWidth])

  return (
    <aside
      role="complementary"
      aria-label="Sources panel"
      className="hidden md:flex relative border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-surface-dark flex-col"
      style={{ width: `${width}px`, flexShrink: 0 }}
    >
      {/* Divider — drag to resize */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sources panel"
        onMouseDown={onResizeStart}
        className={
          'absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-10 transition-colors ' +
          (isResizing ? 'bg-amtrend-blue/50' : 'hover:bg-amtrend-blue/30')
        }
      />

      {/* Tab bar */}
      <TabBar
        tabs={openSources}
        activeId={activeSourceId}
        onActivate={activateSource}
        onClose={closeSource}
        onCollapse={onCollapse}
        mode={mode}
      />

      {/* Active source body */}
      <div className="flex-1 overflow-hidden">
        {mode === 'action' ? (
          <ActionModeBody />
        ) : activeSource ? (
          <SourceRenderer citation={activeSource} />
        ) : (
          <EmptyState />
        )}
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Mobile bottom-sheet variant — visible below md
// ---------------------------------------------------------------------------

function MobileSheet() {
  const {
    openSources,
    activeSourceId,
    activeSource,
    activateSource,
    closeSource,
    setCollapsed,
    mode,
  } = useSourcePane()

  // The pane only renders when expanded; bottom sheet covers 80vh
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Sources"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white dark:bg-surface-dark border-t border-gray-200 dark:border-gray-800 shadow-2xl rounded-t-xl flex flex-col"
      style={{ height: '80vh' }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Sources</span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-label="Close sources"
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-surface-dark-overlay"
        >
          <X size={16} />
        </button>
      </div>
      <TabBar
        tabs={openSources}
        activeId={activeSourceId}
        onActivate={activateSource}
        onClose={closeSource}
        onCollapse={null}
        mode={mode}
      />
      <div className="flex-1 overflow-hidden">
        {mode === 'action' ? (
          <ActionModeBody />
        ) : activeSource ? (
          <SourceRenderer citation={activeSource} />
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab bar — horizontal scroll, X-to-close per tab
// ---------------------------------------------------------------------------

interface TabBarProps {
  tabs: Citation[]
  activeId: string | null
  onActivate: (id: string) => void
  onClose: (id: string) => void
  onCollapse: (() => void) | null
  mode: 'source' | 'action'
}

function TabBar({ tabs, activeId, onActivate, onClose, onCollapse, mode }: TabBarProps) {
  // Auto-scroll active tab into view when it changes
  useEffect(() => {
    if (!activeId) return
    const el = document.querySelector(`[data-source-tab-id="${activeId}"]`)
    el?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
  }, [activeId])

  return (
    <div className="flex items-stretch border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-surface-dark-raised">
      <div className="flex-1 flex overflow-x-auto scrollbar-thin">
        {mode === 'action' && (
          <div className="flex items-center px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border-r border-amber-200 dark:border-amber-900">
            Action pending approval
          </div>
        )}
        {tabs.map(tab => {
          const meta = SOURCE_KIND_META[tab.kind]
          const isActive = tab.id === activeId && mode === 'source'
          return (
            <div
              key={tab.id}
              data-source-tab-id={tab.id}
              className={
                'group flex items-center gap-1.5 pl-3 pr-1 py-1.5 border-r border-gray-200 dark:border-gray-800 ' +
                'cursor-pointer min-w-0 max-w-[200px] transition-colors ' +
                (isActive
                  ? 'bg-white dark:bg-surface-dark border-b-2 border-b-amtrend-blue -mb-px'
                  : 'hover:bg-gray-100 dark:hover:bg-surface-dark-overlay')
              }
              onClick={() => onActivate(tab.id)}
            >
              <span className="text-[9px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500 flex-shrink-0">
                {meta.label}
              </span>
              <span className="text-xs text-gray-700 dark:text-gray-200 truncate">{tab.label}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(tab.id) }}
                aria-label={`Close ${tab.label}`}
                className="p-0.5 rounded opacity-50 hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 flex-shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
      {onCollapse && (
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse sources panel"
          title="Collapse (Esc)"
          className="p-2 border-l border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay text-gray-500 dark:text-gray-400"
        >
          <PanelRightClose size={16} />
        </button>
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 p-6">
      <p className="max-w-[20ch] text-center">
        Click a citation chip in any message to open the source here. Use{' '}
        <kbd className="px-1 rounded bg-gray-100 dark:bg-surface-dark-overlay">[</kbd> /{' '}
        <kbd className="px-1 rounded bg-gray-100 dark:bg-surface-dark-overlay">]</kbd> to cycle,{' '}
        <kbd className="px-1 rounded bg-gray-100 dark:bg-surface-dark-overlay">Esc</kbd> to collapse.
      </p>
    </div>
  )
}

function ActionModeBody() {
  const {
    pendingAction, pendingActionApprovalId, pendingActionExpiresAt, setPendingAction,
  } = useSourcePane()
  if (!pendingAction || !pendingActionApprovalId) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 p-6">
        <p>Awaiting Action Card…</p>
      </div>
    )
  }
  return (
    <ActionCardView
      card={pendingAction}
      approvalId={pendingActionApprovalId}
      expiresAt={pendingActionExpiresAt}
      onResolved={() => setPendingAction(null)}
    />
  )
}

